import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";

const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_tulsa-student-care/artifacts/ly6w2llm_Screenshot%202026-02-13%20at%204%2C17%2C27%E2%80%AFPM-Picsart-BackgroundRemover%20%281%29.png"
    alt="Comforts of Home Logo"
    className="w-14 h-14 object-contain"
  />
);

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Shop", path: "/shop" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50" data-testid="navbar">
      {/* Top Bar - Faint Blue with logo */}
      <div className="bg-[rgba(212,230,240,1)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[90px]">
            {/* Logo - Left aligned */}
            <Link to="/" className="flex items-end gap-2" data-testid="navbar-logo">
              <Logo />
              <div className="flex items-end gap-2">
                <span className="font-heading text-4xl text-[#3F4A52]">Comforts</span>
                <span className="font-accent text-4xl text-[#3F4A52]">of Home</span>
              </div>
            </Link>

            {/* Cart Icon - Right side */}
            <div className="flex justify-end">
              <Link 
                to="/cart" 
                className="relative p-2 text-[#3F4A52] hover:text-[#E58C7C] transition-colors"
                data-testid="cart-icon"
              >
                <ShoppingCart size={26} />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 bg-[#E58C7C] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Second Bar - Darker Blue with navigation links */}
      <div className="bg-[#9FBFD6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center justify-center gap-12 h-12" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`font-body text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "text-[#3F4A52] underline underline-offset-4"
                    : "text-[#3F4A52] hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex justify-center py-2">
            <button
              className="p-2 text-[#3F4A52]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden pb-4 bg-[#9FBFD6]" data-testid="mobile-nav">
            <div className="flex flex-col items-center gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-body text-base font-medium py-2 transition-colors ${
                    isActive(link.path)
                      ? "text-[#3F4A52] underline underline-offset-4"
                      : "text-[#3F4A52] hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>

      {/* Third Bar - Peach accent bar */}
      <div className="bg-[#E58C7C] h-[10px]"></div>
    </header>
  );
};

export default Navbar;
